/**
 * FormationPackageForm — Tabbed / stepper form to create/edit a
 * comprehensive company-formation package (matches UAE freezone /
 * mainland offerings).
 *
 * Feb 2026 / iter-247.
 *
 * Route: /control/company-setup/formation-packages/new
 *        /control/company-setup/formation-packages/:id/edit
 *
 * Backend: /api/admin/formation-packages (POST / PUT / GET)
 *
 * Sections (5 tabs):
 *   1. General Info      — basic + jurisdiction + legal form
 *   2. Activities        — activities + facility
 *   3. Pricing Matrix    — multi-year price rows + renewal + surcharge
 *   4. Visa Setup        — visas + insurance tiers
 *   5. Inclusions + Terms — service inclusions + payment terms + WYSIWYG
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Save, Loader2, ArrowLeft, Copy, Trash2, Plus, X, Info,
  Building2, Layers, Wallet, Users, CheckCircle2, FileText, Package,
} from 'lucide-react';

import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { AUTHORITIES, LICENSE_TYPES, LEGAL_FORMS, FACILITY_TYPES, ADVISORY_TOPICS, PARTNER_BANKS } from './formationPackageOptions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'general',    labelAr: 'المعلومات الأساسية', labelEn: 'General',    Icon: Building2 },
  { key: 'activities', labelAr: 'الأنشطة والمكاتب',   labelEn: 'Activities', Icon: Layers    },
  { key: 'pricing',    labelAr: 'الأسعار',            labelEn: 'Pricing',    Icon: Wallet    },
  { key: 'visas',      labelAr: 'التأشيرات',          labelEn: 'Visas',      Icon: Users     },
  { key: 'inclusions', labelAr: 'الشمول والشروط',    labelEn: 'Inclusions', Icon: CheckCircle2 },
];

const EMPTY = {
  name_ar: '', name_en: '', bundle_code: '', authority: '',
  // iter-253 — dynamic country/region/authority IDs (from company_setup_inputs)
  country_id: '', country_ar: '', country_en: '',
  region_id: '', region_ar: '', region_en: '',
  authority_id: '',
  license_types: [], excluded_sectors: [], legal_forms: [], shareholder_types: [],
  max_shareholders: 50, manager_criteria: 'either', status: 'draft',
  max_activities: 0, mix_match_activities: false, facility_type: '', facility_inclusions: '',
  // iter-253 — office pricing mode + values
  office_pricing_mode: 'included',
  office_fixed_price: 0, office_price_min: 0, office_price_max: 0, office_price_per_sqm: 0,
  tenure_prices: [{ years: 1, price: 0 }],
  renewal_policy: 'same_as_new', renewal_value: 0,
  federal_surcharge: 0, platform_markup: 0,
  included_visas: 0, visa_categories: [], vip_medical: false, vip_medical_location: '',
  extra_visa_capacity: 0, dependent_visa_discount_pct: 0, insurance_tiers: [],
  bank_account_assistance: false, bank_notes: '',
  corporate_tax_registration: false, corporate_tax_filing: 'none',
  free_amendments_per_year: 0, business_advisory: false, advisory_topics: [],
  installment_available: false, installment_rules: [], zero_percent_banks: [],
  terms_and_exclusions: '',
};

export default function FormationPackageForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [activeTab, setActiveTab] = useState('general');
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [validationErr, setValidationErr] = useState({});
  // iter-253 — Dynamic Country/Region/Authority tree from company_setup_inputs
  const [setupTree, setSetupTree] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/company-setup-inputs/active-tree`, { headers: getAuthHeaders() });
        setSetupTree(r.data?.data || []);
      } catch (err) {
        // Non-blocking — the form still works with the static AUTHORITIES list
        console.warn('[formation-package] setup-inputs tree unavailable', err.message);
      }
    })();
  }, []);

  // Pre-fill authority from query string (?authority=rakez) when creating.
  useEffect(() => {
    const a = searchParams.get('authority');
    if (a && !isEdit) setForm((f) => ({ ...f, authority: a }));
  }, [isEdit, searchParams]);

  // iter-249 — Support `?back=<path>` so callers (like the RAKEZ tab) can
  // return the operator to their originating list after save/cancel/delete.
  const backPath = searchParams.get('back') || '/control/company-setup/formation-packages';

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/formation-packages/${id}`, { headers: getAuthHeaders() });
        setForm({ ...EMPTY, ...(r.data.data || {}) });
      } catch (err) {
        toast.error(err?.response?.data?.detail || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInArray = (k, v) => setForm((f) => ({
    ...f,
    [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v],
  }));

  const validate = () => {
    const errs = {};
    if (!form.name_ar.trim())     errs.name_ar     = t('الاسم بالعربي مطلوب', 'Arabic name required');
    if (!form.name_en.trim())     errs.name_en     = t('الاسم بالإنجليزي مطلوب', 'English name required');
    if (!form.bundle_code.trim()) errs.bundle_code = t('كود الباقة مطلوب',      'Bundle code required');
    if (!form.authority)          errs.authority   = t('الجهة مطلوبة',         'Authority required');
    (form.tenure_prices || []).forEach((tp, idx) => {
      if (tp.price < 0) errs[`tenure_${idx}`] = t('السعر لا يمكن أن يكون سالباً', 'Price cannot be negative');
    });
    setValidationErr(errs);
    if (Object.keys(errs).length) {
      toast.error(t('راجع الحقول المطلوبة', 'Please review required fields'));
      // Auto-jump to the first tab with an error
      if (errs.name_ar || errs.name_en || errs.bundle_code || errs.authority) setActiveTab('general');
      else if (Object.keys(errs).some((k) => k.startsWith('tenure_'))) setActiveTab('pricing');
      return false;
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      // normalise numeric fields
      payload.max_shareholders     = Number(payload.max_shareholders) || 0;
      payload.max_activities       = Number(payload.max_activities)   || 0;
      payload.included_visas       = Number(payload.included_visas)   || 0;
      payload.extra_visa_capacity  = Number(payload.extra_visa_capacity) || 0;
      payload.federal_surcharge    = Number(payload.federal_surcharge) || 0;
      payload.platform_markup      = Number(payload.platform_markup)   || 0;
      payload.free_amendments_per_year = Number(payload.free_amendments_per_year) || 0;
      payload.renewal_value        = Number(payload.renewal_value)     || 0;
      payload.dependent_visa_discount_pct = Number(payload.dependent_visa_discount_pct) || 0;
      // iter-253 — office pricing numeric normalisation
      payload.office_fixed_price   = Number(payload.office_fixed_price) || 0;
      payload.office_price_min     = Number(payload.office_price_min)   || 0;
      payload.office_price_max     = Number(payload.office_price_max)   || 0;
      payload.office_price_per_sqm = Number(payload.office_price_per_sqm) || 0;
      payload.tenure_prices = (payload.tenure_prices || []).map((tp) => ({
        years: Number(tp.years) || 1,
        price: Number(tp.price) || 0,
      }));

      const url = isEdit ? `${API}/admin/formation-packages/${id}` : `${API}/admin/formation-packages`;
      const method = isEdit ? 'put' : 'post';
      const r = await axios[method](url, payload, { headers: getAuthHeaders() });
      toast.success(r.data.message || t('تم الحفظ', 'Saved'));
      // iter-249 — On create success, jump straight to edit mode of the
      // newly-saved package (keeps `?back=` so cancel still routes correctly).
      if (!isEdit && r.data.data?.id) {
        const editPath = `/control/company-setup/formation-packages/${r.data.data.id}/edit`;
        const preserveBack = searchParams.get('back');
        navigate(preserveBack ? `${editPath}?back=${encodeURIComponent(preserveBack)}` : editPath);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async () => {
    if (!isEdit) return;
    if (!window.confirm(t('نسخ الباقة إلى باقة جديدة (Draft)؟', 'Duplicate this package into a new Draft?'))) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/formation-packages/${id}/duplicate`, {}, { headers: getAuthHeaders() });
      toast.success(t('تم النسخ', 'Duplicated'));
      navigate(`/control/company-setup/formation-packages/${r.data.data.id}/edit`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const hardDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm(t('حذف نهائي للباقة؟', 'Permanently delete this package?'))) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/formation-packages/${id}`, { headers: getAuthHeaders() });
      toast.success(t('تم الحذف', 'Deleted'));
      navigate(backPath);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const activeIdx = useMemo(() => TABS.findIndex((x) => x.key === activeTab), [activeTab]);

  return (
    <div className="min-h-screen bg-[#050510] text-white pb-16" dir={isAr ? 'rtl' : 'ltr'}>
      <DashboardHeader showBackButton backPath={backPath} />

      <div className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'إعداد الشركات', label_en: 'Company Setup', path: '/control/company-setup' },
            { label_ar: 'باقات التأسيس', label_en: 'Formation Packages', path: '/control/company-setup/formation-packages' },
            { label_ar: isEdit ? (isAr ? (form.name_ar || 'تعديل') : (form.name_en || 'Edit')) : (isAr ? 'إنشاء باقة' : 'New Package'), path: null },
          ]} />
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
          </div>
        ) : (
          <>
            {/* Header + primary actions */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-5 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
                  <Package className="w-6 h-6 text-amber-300" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">
                    {isEdit ? t('تعديل باقة تأسيس', 'Edit Formation Package') : t('إنشاء باقة تأسيس', 'New Formation Package')}
                  </h1>
                  <p className="text-[11px] text-white/50 font-mono mt-0.5">
                    {form.bundle_code || t('كود سيتم إنشاؤه', 'Bundle code TBD')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {isEdit && (
                  <>
                    <Button variant="outline" onClick={duplicate} disabled={saving}
                            className="border-white/10 bg-transparent text-white/80 hover:bg-white/5"
                            data-testid="pkg-duplicate-btn">
                      <Copy className="w-4 h-4 me-2" />
                      {t('نسخ', 'Duplicate')}
                    </Button>
                    <Button variant="outline" onClick={hardDelete} disabled={saving}
                            className="border-rose-500/30 bg-transparent text-rose-300 hover:bg-rose-500/10"
                            data-testid="pkg-delete-btn">
                      <Trash2 className="w-4 h-4 me-2" />
                      {t('حذف', 'Delete')}
                    </Button>
                  </>
                )}
                <Button onClick={save} disabled={saving}
                        className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
                        data-testid="pkg-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Save className="w-4 h-4 me-2" />}
                  {t('حفظ الباقة', 'Save Package')}
                </Button>
              </div>
            </div>

            {/* Stepper header */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 mb-5 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max">
                {TABS.map((tb, i) => {
                  const Icon = tb.Icon;
                  const active = tb.key === activeTab;
                  const done   = i < activeIdx;
                  return (
                    <button
                      key={tb.key}
                      onClick={() => setActiveTab(tb.key)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                        active ? 'bg-amber-500/15 border border-amber-500/40 text-amber-200' :
                        done   ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-300' :
                                 'bg-white/[0.02] border border-white/10 text-white/60 hover:text-white/80'
                      }`}
                      data-testid={`pkg-tab-${tb.key}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{isAr ? tb.labelAr : tb.labelEn}</span>
                      <span className="text-[10px] opacity-60 font-mono">{i + 1}/{TABS.length}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <motion.section
              key={activeTab}
              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
            >
              {activeTab === 'general'    && <GeneralSection    form={form} set={set} toggleInArray={toggleInArray} t={t} isAr={isAr} err={validationErr} setupTree={setupTree} />}
              {activeTab === 'activities' && <ActivitiesSection form={form} set={set} t={t} isAr={isAr} />}
              {activeTab === 'pricing'    && <PricingSection    form={form} set={set} t={t} isAr={isAr} err={validationErr} />}
              {activeTab === 'visas'      && <VisasSection      form={form} set={set} toggleInArray={toggleInArray} t={t} isAr={isAr} />}
              {activeTab === 'inclusions' && <InclusionsSection form={form} set={set} toggleInArray={toggleInArray} t={t} isAr={isAr} />}
            </motion.section>

            {/* Prev / Next footer */}
            <div className="flex items-center justify-between mt-4 gap-3">
              <Button
                variant="outline"
                disabled={activeIdx === 0}
                onClick={() => setActiveTab(TABS[activeIdx - 1].key)}
                className="border-white/10 bg-transparent text-white/70 hover:bg-white/5"
              >
                <ArrowLeft className="w-4 h-4 me-2" />
                {t('السابق', 'Previous')}
              </Button>
              {activeIdx < TABS.length - 1 ? (
                <Button
                  onClick={() => setActiveTab(TABS[activeIdx + 1].key)}
                  className="bg-white/10 hover:bg-white/15 text-white"
                >
                  {t('التالي', 'Next')}
                </Button>
              ) : (
                <Button onClick={save} disabled={saving}
                        className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Save className="w-4 h-4 me-2" />}
                  {t('حفظ الباقة', 'Save Package')}
                </Button>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
   SUB-SECTIONS — one per tab. Kept in the same file to preserve the
   package's tight coupling (they only make sense together).
   ──────────────────────────────────────────────────────────────────── */

function FieldLabel({ children, req, hint }) {
  return (
    <div className="flex items-center justify-between mb-1.5">
      <label className="text-xs text-white/70 flex items-center gap-1">
        {children}
        {req && <span className="text-rose-400">*</span>}
      </label>
      {hint && <span className="text-[10px] text-white/40 flex items-center gap-1"><Info className="w-3 h-3" />{hint}</span>}
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
        active
          ? 'bg-amber-500/20 border-amber-500/40 text-amber-200'
          : 'bg-white/[0.02] border-white/10 text-white/60 hover:text-white/80'
      }`}
    >{children}</button>
  );
}

/* ── Tab 1 — GeneralSection ─────────────────────────────────────── */
function GeneralSection({ form, set, toggleInArray, t, isAr, err, setupTree = [] }) {
  // iter-253 — cascading dropdowns from setup-inputs tree
  const country = setupTree.find((c) => c.id === form.country_id) || null;
  const regions = country?.regions || [];
  const region = regions.find((r) => r.id === form.region_id) || null;
  const authorities = region?.authorities || [];

  const onSelectCountry = (id) => {
    const c = setupTree.find((x) => x.id === id) || null;
    set('country_id', id);
    set('country_ar', c?.name_ar || '');
    set('country_en', c?.name_en || '');
    // reset children
    set('region_id', '');
    set('region_ar', '');
    set('region_en', '');
    set('authority_id', '');
    set('authority_ar', '');
    set('authority_en', '');
  };
  const onSelectRegion = (id) => {
    const r = regions.find((x) => x.id === id) || null;
    set('region_id', id);
    set('region_ar', r?.name_ar || '');
    set('region_en', r?.name_en || '');
    set('authority_id', '');
    set('authority_ar', '');
    set('authority_en', '');
  };
  const onSelectAuthorityDynamic = (id) => {
    const a = authorities.find((x) => x.id === id) || null;
    set('authority_id', id);
    set('authority_ar', a?.name_ar || '');
    set('authority_en', a?.name_en || '');
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <FieldLabel req>{t('الاسم بالعربي', 'Name (AR)')}</FieldLabel>
        <Input value={form.name_ar} onChange={(e) => set('name_ar', e.target.value)}
               placeholder="باقة ماكس كو 1" data-testid="pkg-name-ar" />
        {err.name_ar && <p className="text-[11px] text-rose-400 mt-1">{err.name_ar}</p>}
      </div>
      <div>
        <FieldLabel req>{t('الاسم بالإنجليزي', 'Name (EN)')}</FieldLabel>
        <Input value={form.name_en} onChange={(e) => set('name_en', e.target.value)}
               placeholder="Max Co 1" data-testid="pkg-name-en" />
        {err.name_en && <p className="text-[11px] text-rose-400 mt-1">{err.name_en}</p>}
      </div>
      <div>
        <FieldLabel req hint={t('لا يتكرر', 'unique')}>{t('كود الباقة (SKU)', 'Bundle code (SKU)')}</FieldLabel>
        <Input value={form.bundle_code} onChange={(e) => set('bundle_code', e.target.value.toUpperCase())}
               placeholder="MAX-CO-1" className="font-mono" data-testid="pkg-bundle-code" />
        {err.bundle_code && <p className="text-[11px] text-rose-400 mt-1">{err.bundle_code}</p>}
      </div>
      <div>
        <FieldLabel req>{t('الجهة (كود)', 'Authority (slug)')}</FieldLabel>
        <select value={form.authority} onChange={(e) => set('authority', e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white text-sm"
                data-testid="pkg-authority">
          <option value="">{t('اختر…', 'Select…')}</option>
          {AUTHORITIES.map((a) => <option key={a.slug} value={a.slug}>{isAr ? a.ar : a.en}</option>)}
        </select>
        {err.authority && <p className="text-[11px] text-rose-400 mt-1">{err.authority}</p>}
      </div>

      {/* iter-253 — Dynamic cascading dropdowns from company_setup_inputs */}
      <div className="md:col-span-2">
        <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.03] p-4">
          <p className="text-[11px] text-cyan-300/80 mb-3 flex items-center gap-1">
            <Info className="h-3 w-3" />
            {t(
              'الاختيار الديناميكي — البيانات تُحمّل من: التحكم ← CLOUDGM FZ LLC ← تأسيس الشركات ← قائمة مدخلات تسجيل الشركات.',
              'Dynamic selection — data loads from: Control ← CLOUDGM FZ LLC ← Company Setup ← Registration Inputs List.',
            )}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <FieldLabel>{t('الدولة', 'Country')}</FieldLabel>
              <select value={form.country_id || ''} onChange={(e) => onSelectCountry(e.target.value)}
                      className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white text-sm"
                      data-testid="pkg-country-dyn">
                <option value="">{t('اختر الدولة…', 'Select country…')}</option>
                {setupTree.map((c) => (
                  <option key={c.id} value={c.id}>{isAr ? c.name_ar : c.name_en}</option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel>{t('الإمارة / المنطقة', 'Emirate / Region')}</FieldLabel>
              <select value={form.region_id || ''} onChange={(e) => onSelectRegion(e.target.value)}
                      disabled={!form.country_id}
                      className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white text-sm disabled:opacity-40"
                      data-testid="pkg-region-dyn">
                <option value="">{t('اختر الإمارة…', 'Select emirate…')}</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>{isAr ? r.name_ar : r.name_en}</option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel>{t('الجهة', 'Authority')}</FieldLabel>
              <select value={form.authority_id || ''} onChange={(e) => onSelectAuthorityDynamic(e.target.value)}
                      disabled={!form.region_id}
                      className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white text-sm disabled:opacity-40"
                      data-testid="pkg-authority-dyn">
                <option value="">{t('اختر الجهة…', 'Select authority…')}</option>
                {authorities.map((a) => (
                  <option key={a.id} value={a.id}>{isAr ? a.name_ar : a.name_en}</option>
                ))}
              </select>
            </div>
          </div>
          {setupTree.length === 0 && (
            <p className="text-[11px] text-amber-300/70 mt-2">
              {t(
                'لا توجد مدخلات محفوظة بعد — أضِفها من: التحكم ← CLOUDGM FZ LLC ← تأسيس الشركات.',
                'No inputs saved yet — add them from: Control ← CLOUDGM FZ LLC ← Company Setup.',
              )}
            </p>
          )}
        </div>
      </div>
      <div className="md:col-span-2">
        <FieldLabel>{t('أنواع الرخص المسموحة', 'Permitted license types')}</FieldLabel>
        <div className="flex flex-wrap gap-1.5">
          {LICENSE_TYPES.map((lt) => (
            <Chip key={lt.key} active={form.license_types.includes(lt.key)}
                  onClick={() => toggleInArray('license_types', lt.key)}
                  testid={`pkg-lt-${lt.key}`}>
              {isAr ? lt.ar : lt.en}
            </Chip>
          ))}
        </div>
      </div>
      <div className="md:col-span-2">
        <FieldLabel>{t('قطاعات مستثناة', 'Excluded sectors')}</FieldLabel>
        <div className="flex flex-wrap gap-1.5">
          {['industrial', 'financial', 'medical', 'legal'].map((k) => (
            <Chip key={k} active={form.excluded_sectors.includes(k)}
                  onClick={() => toggleInArray('excluded_sectors', k)}
                  testid={`pkg-excl-${k}`}>
              {k}
            </Chip>
          ))}
        </div>
      </div>
      <div className="md:col-span-2">
        <FieldLabel>{t('الأشكال القانونية المسموحة', 'Permitted legal forms')}</FieldLabel>
        <div className="flex flex-wrap gap-1.5">
          {LEGAL_FORMS.map((lf) => (
            <Chip key={lf.key} active={form.legal_forms.includes(lf.key)}
                  onClick={() => toggleInArray('legal_forms', lf.key)}
                  testid={`pkg-lf-${lf.key}`}>
              {isAr ? lf.ar : lf.en}
            </Chip>
          ))}
        </div>
      </div>
      <div>
        <FieldLabel>{t('أنواع المساهمين', 'Shareholder types')}</FieldLabel>
        <div className="flex gap-2 items-center">
          {['individual', 'corporate'].map((k) => (
            <label key={k} className="flex items-center gap-1 text-xs text-white/70 cursor-pointer">
              <input type="checkbox" checked={form.shareholder_types.includes(k)}
                     onChange={() => toggleInArray('shareholder_types', k)}
                     data-testid={`pkg-sh-${k}`} />
              {t(k === 'individual' ? 'فرد' : 'شركة', k === 'individual' ? 'Individual' : 'Corporate')}
            </label>
          ))}
        </div>
      </div>
      <div>
        <FieldLabel>{t('حد أقصى للمساهمين', 'Max shareholders')}</FieldLabel>
        <Input type="number" min={1} value={form.max_shareholders}
               onChange={(e) => set('max_shareholders', e.target.value)}
               data-testid="pkg-max-shareholders" />
      </div>
      <div>
        <FieldLabel>{t('معايير المدير', 'Manager criteria')}</FieldLabel>
        <select value={form.manager_criteria} onChange={(e) => set('manager_criteria', e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white text-sm"
                data-testid="pkg-manager">
          <option value="either">{t('أي منهما', 'Either')}</option>
          <option value="shareholder">{t('من المساهمين', 'Shareholder only')}</option>
          <option value="non_shareholder">{t('من غير المساهمين', 'Non-shareholder only')}</option>
        </select>
      </div>
      <div>
        <FieldLabel>{t('الحالة', 'Status')}</FieldLabel>
        <select value={form.status} onChange={(e) => set('status', e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white text-sm"
                data-testid="pkg-status">
          <option value="active">{t('نشطة', 'Active')}</option>
          <option value="draft">{t('مسودة', 'Draft')}</option>
          <option value="archived">{t('مؤرشفة', 'Archived')}</option>
        </select>
      </div>
    </div>
  );
}

/* ── Tab 2 — ActivitiesSection ──────────────────────────────────── */
function ActivitiesSection({ form, set, t, isAr }) {
  // iter-253 — Virtual/flexi offices don't need a physical office price
  const isVirtual = form.facility_type === 'virtual_flexi';
  const OFFICE_PRICING_MODES = [
    { key: 'included', ar: 'شامل سعر الباقة', en: 'Included in package price' },
    { key: 'fixed',    ar: 'سعر ثابت',        en: 'Fixed price' },
    { key: 'range',    ar: 'سعر من والى',    en: 'Price range (min – max)' },
    { key: 'per_sqm',  ar: 'سعر على المتر المربع', en: 'Price per sq/m' },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <FieldLabel>{t('عدد الأنشطة المسموحة', 'Max activities allowed')}</FieldLabel>
        <Input type="number" min={0} value={form.max_activities}
               onChange={(e) => set('max_activities', e.target.value)}
               data-testid="pkg-max-activities" />
      </div>
      <div>
        <FieldLabel>{t('السماح بخلط الأنشطة من فئات مختلفة', 'Mix & match across categories')}</FieldLabel>
        <label className="flex items-center gap-2 mt-2">
          <input type="checkbox" checked={form.mix_match_activities}
                 onChange={(e) => set('mix_match_activities', e.target.checked)}
                 data-testid="pkg-mix-match" />
          <span className="text-xs text-white/70">{t('نعم', 'Enabled')}</span>
        </label>
      </div>
      <div>
        <FieldLabel>{t('نوع مكان العمل', 'Facility type')}</FieldLabel>
        <select value={form.facility_type} onChange={(e) => {
          set('facility_type', e.target.value);
          // Auto-reset pricing mode when switching to virtual
          if (e.target.value === 'virtual_flexi') set('office_pricing_mode', 'included');
        }}
                className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white text-sm"
                data-testid="pkg-facility-type">
          <option value="">{t('اختر…', 'Select…')}</option>
          {FACILITY_TYPES.map((f) => <option key={f.key} value={f.key}>{isAr ? f.ar : f.en}</option>)}
        </select>
      </div>
      <div className="md:col-span-2">
        <FieldLabel>{t('ما يشمله مكان العمل', 'Facility inclusions (bullets / paragraph)')}</FieldLabel>
        <textarea value={form.facility_inclusions}
                  onChange={(e) => set('facility_inclusions', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white text-sm"
                  placeholder={t('Compass Coworking access, lease agreement for banking…', 'Compass Coworking access, lease agreement for banking…')}
                  data-testid="pkg-facility-inclusions" />
      </div>

      {/* iter-253 — Office pricing mode */}
      <div className="md:col-span-2">
        <div className={`rounded-xl border p-4 ${isVirtual
            ? 'border-white/10 bg-white/[0.01] opacity-60'
            : 'border-amber-500/25 bg-amber-500/[0.03]'}`}>
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-white">
                {t('تسعير المكتب', 'Office Pricing')}
              </p>
              <p className="text-[11px] text-white/50 mt-0.5">
                {isVirtual
                  ? t('المكتب الافتراضي لا يحتاج سعر — تم تعطيل الخيارات.',
                      'Virtual / flexi offices do not require a price — options disabled.')
                  : t('اختر طريقة تسعير المكتب. اختياري لكل باقة.',
                      'Pick how this package prices its office. Optional per package.')}
              </p>
            </div>
          </div>

          <select value={form.office_pricing_mode || 'included'}
                  onChange={(e) => set('office_pricing_mode', e.target.value)}
                  disabled={isVirtual}
                  className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white text-sm disabled:opacity-40"
                  data-testid="pkg-office-pricing-mode">
            {OFFICE_PRICING_MODES.map((m) => (
              <option key={m.key} value={m.key}>{isAr ? m.ar : m.en}</option>
            ))}
          </select>

          {!isVirtual && form.office_pricing_mode === 'fixed' && (
            <div className="mt-3">
              <FieldLabel>{t('السعر الثابت (د.إ)', 'Fixed price (AED)')}</FieldLabel>
              <Input type="number" min={0} step="0.01" value={form.office_fixed_price}
                     onChange={(e) => set('office_fixed_price', e.target.value)}
                     data-testid="pkg-office-fixed-price" />
            </div>
          )}

          {!isVirtual && form.office_pricing_mode === 'range' && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>{t('من (د.إ)', 'From (AED)')}</FieldLabel>
                <Input type="number" min={0} step="0.01" value={form.office_price_min}
                       onChange={(e) => set('office_price_min', e.target.value)}
                       data-testid="pkg-office-price-min" />
              </div>
              <div>
                <FieldLabel>{t('إلى (د.إ)', 'To (AED)')}</FieldLabel>
                <Input type="number" min={0} step="0.01" value={form.office_price_max}
                       onChange={(e) => set('office_price_max', e.target.value)}
                       data-testid="pkg-office-price-max" />
              </div>
            </div>
          )}

          {!isVirtual && form.office_pricing_mode === 'per_sqm' && (
            <div className="mt-3">
              <FieldLabel>{t('السعر لكل متر مربع (د.إ)', 'Price per sq/m (AED)')}</FieldLabel>
              <Input type="number" min={0} step="0.01" value={form.office_price_per_sqm}
                     onChange={(e) => set('office_price_per_sqm', e.target.value)}
                     data-testid="pkg-office-price-per-sqm" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Tab 3 — PricingSection ─────────────────────────────────────── */
function PricingSection({ form, set, t, isAr, err }) {
  const addRow = () => set('tenure_prices', [...(form.tenure_prices || []), { years: 1, price: 0 }]);
  const removeRow = (i) => set('tenure_prices', form.tenure_prices.filter((_, x) => x !== i));
  const update = (i, k, v) => set('tenure_prices', form.tenure_prices.map((row, x) => x === i ? { ...row, [k]: v } : row));

  return (
    <div className="grid grid-cols-1 gap-4">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Wallet className="w-4 h-4 text-amber-400" />
            {t('جدول الأسعار حسب المدة', 'Multi-year price matrix')}
          </h3>
          <Button size="sm" onClick={addRow} className="bg-amber-500/20 border border-amber-500/40 text-amber-200"
                  data-testid="pkg-add-tenure">
            <Plus className="w-3 h-3 me-1" /> {t('صف جديد', 'Add row')}
          </Button>
        </div>
        <div className="rounded-lg border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02] border-b border-white/10">
              <tr>
                <th className="px-3 py-2 text-start text-[11px] text-white/60 uppercase w-40">{t('المدة (سنوات)', 'Tenure (years)')}</th>
                <th className="px-3 py-2 text-start text-[11px] text-white/60 uppercase">{t('السعر (د.إ)', 'Price (AED)')}</th>
                <th className="px-3 py-2 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {(form.tenure_prices || []).map((row, i) => (
                <tr key={i} className="border-b border-white/5" data-testid={`pkg-tenure-row-${i}`}>
                  <td className="px-3 py-2">
                    <Input type="number" min={1} value={row.years}
                           onChange={(e) => update(i, 'years', e.target.value)}
                           className="h-8 text-sm"
                           data-testid={`pkg-tenure-years-${i}`} />
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" min={0} step="0.01" value={row.price}
                           onChange={(e) => update(i, 'price', e.target.value)}
                           className="h-8 text-sm font-mono"
                           data-testid={`pkg-tenure-price-${i}`} />
                    {err[`tenure_${i}`] && <p className="text-[10px] text-rose-400 mt-0.5">{err[`tenure_${i}`]}</p>}
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => removeRow(i)} className="p-1 rounded hover:bg-rose-500/10 text-rose-400"
                            data-testid={`pkg-tenure-remove-${i}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {form.tenure_prices?.length === 0 && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-white/40 text-xs">{t('لا صفوف بعد — اضغط "صف جديد"', 'No rows — click "Add row"')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <FieldLabel>{t('سياسة التجديد', 'Renewal policy')}</FieldLabel>
          <select value={form.renewal_policy} onChange={(e) => set('renewal_policy', e.target.value)}
                  className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white text-sm"
                  data-testid="pkg-renewal-policy">
            <option value="same_as_new">{t('نفس سعر الباقة الجديدة', 'Same as new package price')}</option>
            <option value="custom">{t('سعر تجديد مخصص', 'Custom renewal price (AED)')}</option>
            <option value="percent_discount">{t('نسبة خصم %', 'Percentage discount %')}</option>
          </select>
        </div>
        {form.renewal_policy !== 'same_as_new' && (
          <div>
            <FieldLabel>{form.renewal_policy === 'percent_discount' ? t('نسبة الخصم', 'Discount %') : t('السعر المخصص', 'Custom price (AED)')}</FieldLabel>
            <Input type="number" min={0} value={form.renewal_value}
                   onChange={(e) => set('renewal_value', e.target.value)}
                   data-testid="pkg-renewal-value" />
          </div>
        )}
        <div>
          <FieldLabel hint={t('اتحادية', 'federal')}>{t('رسوم اتحادية إلزامية', 'Federal surcharge (AED/yr)')}</FieldLabel>
          <Input type="number" min={0} value={form.federal_surcharge}
                 onChange={(e) => set('federal_surcharge', e.target.value)}
                 data-testid="pkg-federal-surcharge" />
        </div>
        <div>
          <FieldLabel hint={t('اختياري', 'optional')}>{t('هامش المنصة / العمولة', 'Platform markup')}</FieldLabel>
          <Input type="number" min={0} value={form.platform_markup}
                 onChange={(e) => set('platform_markup', e.target.value)}
                 data-testid="pkg-platform-markup" />
        </div>
      </div>
    </div>
  );
}

/* ── Tab 4 — VisasSection ───────────────────────────────────────── */
function VisasSection({ form, set, toggleInArray, t, isAr }) {
  const addTier = () => set('insurance_tiers', [...(form.insurance_tiers || []), {
    salary_range_label: '', salary_threshold: 4000, is_below_or_equal: true, fee_by_year: {},
  }]);
  const removeTier = (i) => set('insurance_tiers', form.insurance_tiers.filter((_, x) => x !== i));
  const updateTier = (i, k, v) => set('insurance_tiers', form.insurance_tiers.map((row, x) => x === i ? { ...row, [k]: v } : row));
  const updateTierFee = (i, year, v) => updateTier(i, 'fee_by_year', { ...(form.insurance_tiers[i].fee_by_year || {}), [year]: Number(v) || 0 });

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <FieldLabel>{t('عدد التأشيرات المشمولة', 'Included visas')}</FieldLabel>
          <Input type="number" min={0} value={form.included_visas}
                 onChange={(e) => set('included_visas', e.target.value)}
                 data-testid="pkg-included-visas" />
        </div>
        <div>
          <FieldLabel>{t('تأشيرات إضافية (قدرة)', 'Extra visa capacity')}</FieldLabel>
          <Input type="number" min={0} value={form.extra_visa_capacity}
                 onChange={(e) => set('extra_visa_capacity', e.target.value)}
                 data-testid="pkg-extra-visas" />
        </div>
        <div>
          <FieldLabel>{t('خصم تأشيرة تابع (%)', 'Dependent visa discount %')}</FieldLabel>
          <Input type="number" min={0} max={100} value={form.dependent_visa_discount_pct}
                 onChange={(e) => set('dependent_visa_discount_pct', e.target.value)}
                 data-testid="pkg-dep-discount" />
        </div>
      </div>
      <div>
        <FieldLabel>{t('فئات التأشيرات', 'Visa categories')}</FieldLabel>
        <div className="flex flex-wrap gap-1.5">
          {['investor', 'partner', 'employee'].map((k) => (
            <Chip key={k} active={form.visa_categories.includes(k)}
                  onClick={() => toggleInArray('visa_categories', k)}
                  testid={`pkg-vc-${k}`}>
              {t({ investor: 'مستثمر', partner: 'شريك', employee: 'موظف' }[k], k)}
            </Chip>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex items-center gap-2 text-xs text-white/70">
          <input type="checkbox" checked={form.vip_medical}
                 onChange={(e) => set('vip_medical', e.target.checked)}
                 data-testid="pkg-vip-medical" />
          {t('طبي VIP وتسريع الإصدار', 'VIP medical & fast-track')}
        </label>
        {form.vip_medical && (
          <Input value={form.vip_medical_location}
                 onChange={(e) => set('vip_medical_location', e.target.value)}
                 placeholder={t('موقع الطبي VIP (مثال: دبي)', 'VIP medical location (e.g. Dubai)')}
                 data-testid="pkg-vip-location" />
        )}
      </div>

      {/* Insurance tiers matrix */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-white">{t('طبقات تأمين WPI', 'WPI insurance tiers')}</h3>
          <Button size="sm" onClick={addTier} className="bg-white/10 border border-white/10 text-white/80"
                  data-testid="pkg-add-insurance-tier">
            <Plus className="w-3 h-3 me-1" /> {t('طبقة جديدة', 'Add tier')}
          </Button>
        </div>
        <div className="space-y-2">
          {(form.insurance_tiers || []).map((row, i) => (
            <div key={i} className="rounded-lg border border-white/10 p-3 bg-white/[0.02]"
                 data-testid={`pkg-insurance-tier-${i}`}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <Input value={row.salary_range_label}
                       onChange={(e) => updateTier(i, 'salary_range_label', e.target.value)}
                       placeholder={t('التسمية (<= 4000 AED)', 'Label (<= 4000 AED)')}
                       className="h-8 text-xs" />
                <Input type="number" value={row.salary_threshold}
                       onChange={(e) => updateTier(i, 'salary_threshold', Number(e.target.value) || 0)}
                       placeholder={t('عتبة الراتب', 'Salary threshold')}
                       className="h-8 text-xs" />
                <select value={row.is_below_or_equal ? '1' : '0'}
                        onChange={(e) => updateTier(i, 'is_below_or_equal', e.target.value === '1')}
                        className="h-8 text-xs bg-black/40 border border-white/10 text-white rounded-md px-2">
                  <option value="1">{t('<= أقل أو يساوي', '<= less or equal')}</option>
                  <option value="0">{t('> أعلى', '> greater than')}</option>
                </select>
                <button onClick={() => removeTier(i)} className="p-1 rounded hover:bg-rose-500/10 text-rose-400 justify-self-end">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {['1', '2', '5'].map((yr) => (
                  <div key={yr}>
                    <label className="text-[10px] text-white/50">{yr} {t('سنوات', 'yrs')}</label>
                    <Input type="number" value={row.fee_by_year?.[yr] || ''}
                           onChange={(e) => updateTierFee(i, yr, e.target.value)}
                           className="h-8 text-xs font-mono" />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {(!form.insurance_tiers || form.insurance_tiers.length === 0) && (
            <p className="text-xs text-white/40 text-center py-3">{t('لا طبقات — اضغط "طبقة جديدة"', 'No tiers — click "Add tier"')}</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Tab 5 — InclusionsSection ──────────────────────────────────── */
function InclusionsSection({ form, set, toggleInArray, t, isAr }) {
  const addInstallment = () => set('installment_rules', [...(form.installment_rules || []), {
    tenure_years: 1, cdc_count: 1, pdc_count: 2, pdc_interval_days: 90,
  }]);
  const removeInstallment = (i) => set('installment_rules', form.installment_rules.filter((_, x) => x !== i));
  const updateInstallment = (i, k, v) => set('installment_rules', form.installment_rules.map((row, x) => x === i ? { ...row, [k]: Number(v) || 0 } : row));

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex items-center gap-2 text-xs text-white/70">
          <input type="checkbox" checked={form.bank_account_assistance}
                 onChange={(e) => set('bank_account_assistance', e.target.checked)}
                 data-testid="pkg-bank-assist" />
          {t('المساعدة في فتح حساب بنكي مؤسسي', 'Corporate bank account opening assistance')}
        </label>
        {form.bank_account_assistance && (
          <Input value={form.bank_notes}
                 onChange={(e) => set('bank_notes', e.target.value)}
                 placeholder={t('البنوك الشركاء + SLA', 'Partner banks + SLA')}
                 data-testid="pkg-bank-notes" />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="flex items-center gap-2 text-xs text-white/70">
          <input type="checkbox" checked={form.corporate_tax_registration}
                 onChange={(e) => set('corporate_tax_registration', e.target.checked)}
                 data-testid="pkg-ct-reg" />
          {t('تسجيل ضريبة الشركات', 'Corporate tax registration')}
        </label>
        <div>
          <FieldLabel>{t('تقديم إقرار ضريبة الشركات', 'Corporate tax filing')}</FieldLabel>
          <select value={form.corporate_tax_filing} onChange={(e) => set('corporate_tax_filing', e.target.value)}
                  className="w-full h-8 rounded-md bg-black/40 border border-white/10 text-white text-xs px-2"
                  data-testid="pkg-ct-filing">
            <option value="none">{t('غير مشمول', 'Not included')}</option>
            <option value="year1">{t('السنة الأولى فقط', 'Year 1 only')}</option>
            <option value="ongoing">{t('مشمول دائماً', 'Ongoing')}</option>
          </select>
        </div>
        <div>
          <FieldLabel>{t('حصة تعديلات مجانية / سنة', 'Free amendments / year')}</FieldLabel>
          <Input type="number" min={0} value={form.free_amendments_per_year}
                 onChange={(e) => set('free_amendments_per_year', e.target.value)}
                 data-testid="pkg-free-amendments" />
        </div>
      </div>

      <div>
        <label className="flex items-center gap-2 text-xs text-white/70">
          <input type="checkbox" checked={form.business_advisory}
                 onChange={(e) => set('business_advisory', e.target.checked)}
                 data-testid="pkg-advisory" />
          {t('استشارات أعمال', 'Business advisory')}
        </label>
        {form.business_advisory && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {ADVISORY_TOPICS.map((topic) => (
              <Chip key={topic.key} active={form.advisory_topics.includes(topic.key)}
                    onClick={() => toggleInArray('advisory_topics', topic.key)}
                    testid={`pkg-topic-${topic.key}`}>
                {isAr ? topic.ar : topic.en}
              </Chip>
            ))}
          </div>
        )}
      </div>

      <hr className="border-white/5 my-2" />

      {/* Installments */}
      <div>
        <label className="flex items-center gap-2 text-xs text-white/70">
          <input type="checkbox" checked={form.installment_available}
                 onChange={(e) => set('installment_available', e.target.checked)}
                 data-testid="pkg-installment-toggle" />
          {t('التقسيط / الشيكات متاح', 'Installment / cheques available')}
        </label>
        {form.installment_available && (
          <div className="mt-2 space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold text-white/70">{t('قواعد التقسيط', 'Installment rules')}</h4>
              <Button size="sm" onClick={addInstallment} className="bg-white/10 border border-white/10 text-white/80 h-7"
                      data-testid="pkg-add-installment">
                <Plus className="w-3 h-3 me-1" /> {t('قاعدة جديدة', 'Add rule')}
              </Button>
            </div>
            {(form.installment_rules || []).map((row, i) => (
              <div key={i} className="rounded-lg border border-white/10 p-3 bg-white/[0.02] grid grid-cols-2 md:grid-cols-5 gap-2 items-end"
                   data-testid={`pkg-installment-row-${i}`}>
                <div>
                  <label className="text-[10px] text-white/50">{t('المدة (سنوات)', 'Tenure (yrs)')}</label>
                  <Input type="number" min={1} value={row.tenure_years}
                         onChange={(e) => updateInstallment(i, 'tenure_years', e.target.value)}
                         className="h-8 text-xs" />
                </div>
                <div>
                  <label className="text-[10px] text-white/50">CDC</label>
                  <Input type="number" min={0} value={row.cdc_count}
                         onChange={(e) => updateInstallment(i, 'cdc_count', e.target.value)}
                         className="h-8 text-xs" />
                </div>
                <div>
                  <label className="text-[10px] text-white/50">PDC</label>
                  <Input type="number" min={0} value={row.pdc_count}
                         onChange={(e) => updateInstallment(i, 'pdc_count', e.target.value)}
                         className="h-8 text-xs" />
                </div>
                <div>
                  <label className="text-[10px] text-white/50">{t('PDC كل (يوم)', 'PDC every (days)')}</label>
                  <Input type="number" min={0} value={row.pdc_interval_days}
                         onChange={(e) => updateInstallment(i, 'pdc_interval_days', e.target.value)}
                         className="h-8 text-xs" />
                </div>
                <button onClick={() => removeInstallment(i)}
                        className="p-1 rounded hover:bg-rose-500/10 text-rose-400 h-8">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <FieldLabel>{t('بنوك تقسيط 0%', '0% installment partner banks')}</FieldLabel>
        <div className="flex flex-wrap gap-1.5">
          {PARTNER_BANKS.map((b) => (
            <Chip key={b} active={form.zero_percent_banks.includes(b)}
                  onClick={() => toggleInArray('zero_percent_banks', b)}
                  testid={`pkg-bank-${b}`}>
              {b}
            </Chip>
          ))}
        </div>
      </div>

      <div>
        <FieldLabel hint={t('نص كامل — سيظهر بالتقارير', 'shown on quotes / reports')}>
          {t('الشروط والاستثناءات', 'Terms & exclusions')}
        </FieldLabel>
        <textarea value={form.terms_and_exclusions}
                  onChange={(e) => set('terms_and_exclusions', e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white text-sm"
                  placeholder={t('بنود قابلة للتعديل، عناصر غير قابلة للاسترداد، شروط طبية…',
                                  'Editable clauses, non-refundable items, medical conditions…')}
                  data-testid="pkg-terms" />
      </div>
    </div>
  );
}
